import type { FC } from "react";
import { useMemo } from "react";

import { Drawer, shortMonthDayAndYear } from "@similarweb/pro-logic-components";

import { translate } from "app/filters/i18nFilter";
import type { ITabItem } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/side-panel/SidePanelSwitcher";
import { SidePanelSwitcher } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/side-panel/SidePanelSwitcher";
import type { SentimentTab } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sentiment-report/topics-graph/types";
import type { ILLMReportWidget } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { SidePanelEmptyState } from "app/pages/digital-marketing/generative-ai/shared/SidePanelEmptyState";

import { SIDE_PANEL_TABS_CONFIG } from "../config";
import { useTopicPromptsSidePanel } from "../hooks/useTopicPromptsSidePanel";

import { PromptsList } from "./PromptsList";

interface ITopicPromptsSidePanelProps extends Omit<ILLMReportWidget, "reportStatus"> {
    isOpen: boolean;
    topic: string;
    onClose: () => void;
}

export const TopicPromptsSidePanel: FC<ITopicPromptsSidePanelProps> = ({
    isOpen,
    topic,
    onClose,
    campaign,
    campaignConfig,
    reportDuration,
}) => {
    const {
        data,
        isLoading,
        hasError,
        selectedTab,
        sentimentTabs,
        handleTabSelection,
    } = useTopicPromptsSidePanel({
        isOpen,
        topic,
        campaign,
        campaignConfig,
        reportDuration,
    });

    const hasData = data?.[selectedTab]?.total && !hasError;
    const formattedDuration = Object.values(reportDuration)
        .map((value) => value.format(shortMonthDayAndYear))
        .join(" - ");

    const switcherItems = useMemo<ITabItem[]>(
        () =>
            sentimentTabs.map((sentiment) => ({
                id: sentiment,
                label: translate(SIDE_PANEL_TABS_CONFIG[sentiment].tabTextKey, {
                    count: data?.[sentiment]?.total ?? 0,
                }),
            })),
        [sentimentTabs, data],
    );

    return (
        <Drawer
            isOpen={isOpen}
            onOpenChange={(state) => !state && onClose()}
            isLoading={isLoading}
            size="large"
        >
            <Drawer.Header
                title={topic}
                tooltip={formattedDuration}
            />
            <Drawer.SubHeader>
                <SidePanelSwitcher
                    key={selectedTab}
                    items={switcherItems}
                    selectedValue={selectedTab}
                    onItemClick={(tab) => handleTabSelection(tab as SentimentTab)}
                />
            </Drawer.SubHeader>
            <Drawer.Content>
                {hasData ? (
                    <PromptsList
                        campaign={campaign}
                        prompts={data[selectedTab].prompts}
                        selectedTab={selectedTab}
                    />
                ) : (
                    <SidePanelEmptyState
                        title={
                            hasError
                                ? translate("llm.sentiment_report.topics.side-panel.error.title")
                                : translate(`llm.sentiment_report.topics.side-panel.no-data.title.${selectedTab}`)
                        }
                        subtitle={
                            hasError
                                ? translate("llm.sentiment_report.topics.side-panel.error.subtitle")
                                : translate("llm.sentiment_report.topics.side-panel.no-data.subtitle")
                        }
                    />
                )}
            </Drawer.Content>
        </Drawer>
    );
};
